import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import ButtonOmniNew from 'allGlobalComponents/ButtonOmniNew';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import DeleteConfirmationDialog from 'allGlobalComponents/DeleteConfirmationDoalog';
import { useSnackbar, usePagination, useApi } from 'app/hooks';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';

const useStyles = makeStyles(theme => ({
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(2),
        margin: '0 20px',
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap'
    },
    formContent: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5)
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    },
}));

const CONFIG_ID_KEYS = ['whatsapp_config_id', 'facebook_config_id', 'insta_config_id'];

const INITIAL_FORM = {
    name: '',
    appId: '',
    appSecret: '',
    appVersion: 'v',
    webhookUrl: '',
    webhookVerifyToken: '',
    webhookSecret: '',
    configPairs: [
        { key: 'whatsapp_config_id', value: '', required: false, isConfigId: true },
        { key: 'facebook_config_id', value: '', required: false, isConfigId: true },
        { key: 'insta_config_id', value: '', required: false, isConfigId: true },
        { key: 'system_user_token', value: '', required: true }
    ]
};

// Auto-grow rows for textarea fields (matches the chatbot/autoReply message textarea)
const calcRows = (val) => {
    const text = val || '';
    const total = text.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / 50)), 0);
    return Math.max(1, total);
};

const isValidUrl = (str) => {
    if (!str.trim()) return true; // empty is ok (optional)
    try {
        const url = new URL(str);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
};

const isValidAppVersion = (str) => {
    if (!str.trim()) return false;
    return /^v\d+(\.\d+)*$/.test(str.trim());
};

function MetaAppsTab() {
    const classes = useStyles();
    const theme = useTheme();

    // Hooks
    const { snackbar, showSuccess, showError, showWarning } = useSnackbar();
    const { currentParams, setCurrentParams, totalItems, setTotalItems } = usePagination(10);
    const { isLoading, request } = useApi();

    // State
    const [data, setData] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [nameExists, setNameExists] = useState(false);

    // Fetch meta apps
    const fetchMetaApps = () => {
        request({
            module: 'saChannels',
            method: 'metaAppList',
            payload: {},
            onSuccess: (response) => {
                const apps = response.data.data.data || [];
                setData(apps);
                setTotalItems(response.data.data.pagination?.total || apps.length);
            },
            onError: (message) => showError(message || 'Error fetching meta apps')
        });
    };

    useEffect(() => {
        fetchMetaApps();
    }, [refreshKey]);

    // Form handlers
    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (field === 'name') {
            const exists = data.some(app =>
                app.name?.toLowerCase() === value.trim().toLowerCase() &&
                app.id !== selectedRow?.id
            );
            setNameExists(exists);
        }
    };

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const resetForm = () => {
        setFormData(INITIAL_FORM);
        setNameExists(false);
    };

    // Build API payload from form
    const buildPayload = () => {
        if (!formData.name || !formData.appId || !formData.appSecret || !formData.appVersion) return null;

        let configuration = null;
        if (formData.configPairs.length > 0) {
            configuration = {};
            for (const pair of formData.configPairs) {
                if (!pair.key.trim() || !pair.value.trim()) {
                    showWarning('Please fill in all configuration key-value pairs or remove empty ones');
                    return null;
                }
                configuration[pair.key.trim()] = pair.value.trim();
            }
        }

        const payload = {
            name: formData.name,
            appId: formData.appId,
            appSecret: formData.appSecret,
            appVersion: formData.appVersion,
        };

        if (formData.webhookUrl.trim()) payload.webhookUrl = formData.webhookUrl.trim();
        if (formData.webhookVerifyToken.trim()) payload.webhookVerifyToken = formData.webhookVerifyToken.trim();
        if (formData.webhookSecret.trim()) payload.webhookSecret = formData.webhookSecret.trim();
        if (configuration) payload.configuration = configuration;

        return payload;
    };

    // Create
    const handleCreate = () => {
        if (!formData.name || !formData.appId || !formData.appSecret || !formData.appVersion) {
            showWarning('Please fill in all required fields');
            return;
        }

        const payload = buildPayload();
        if (!payload) return;

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'metaAppAdd',
            payload,
            onSuccess: () => {
                showSuccess('Meta app added successfully');
                setCreateOpen(false);
                resetForm();
                fetchMetaApps();
                setSaving(false);
            },
            onError: (message) => {
                showError(message || 'Error adding meta app');
                setSaving(false);
            },
            onFinally: () => setSaving(false)
        });
    };

    // Edit open
    const handleEditOpen = (row) => {
        setSelectedRow(row);

        const existing = (row.configuration && typeof row.configuration === 'object') ? row.configuration : {};
        let configPairs = [];

        // Add config_id variants that already exist (deletable, but locked key)
        CONFIG_ID_KEYS.forEach(k => {
            if (existing[k] != null) {
                configPairs.push({ key: k, value: String(existing[k]), required: false, isConfigId: true });
            }
        });

        // If none of the 3 exist, seed all 3 empty so user can fill at least one
        if (configPairs.length === 0) {
            CONFIG_ID_KEYS.forEach(k => {
                configPairs.push({ key: k, value: '', required: false, isConfigId: true });
            });
        }

        // system_user_token (required)
        configPairs.push({
            key: 'system_user_token',
            value: existing.system_user_token != null ? String(existing.system_user_token) : '',
            required: true
        });

        // Any other custom keys
        Object.entries(existing).forEach(([key, value]) => {
            if (!CONFIG_ID_KEYS.includes(key) && key !== 'system_user_token') {
                configPairs.push({ key, value: String(value), required: false });
            }
        });

        setFormData({
            name: row.name || '',
            appId: row.app_id || '',
            appSecret: row.app_secret || '',
            appVersion: row.app_version || 'v24.0',
            webhookUrl: row.webhook_url || '',
            webhookVerifyToken: row.webhook_verify_token || '',
            webhookSecret: row.webhook_secret || '',
            configPairs
        });
        setNameExists(false);
        setEditOpen(true);
    };

    // Update
    const handleUpdate = () => {
        if (!formData.name || !formData.appId || !formData.appSecret || !formData.appVersion) {
            showWarning('Please fill in all required fields');
            return;
        }

        const payload = buildPayload();
        if (!payload) return;

        payload.id = selectedRow.id;

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'metaAppUpdate',
            payload,
            onSuccess: () => {
                showSuccess('Meta app updated successfully');
                setEditOpen(false);
                resetForm();
                setSelectedRow(null);
                fetchMetaApps();
                setSaving(false);
            },
            onError: (message) => {
                showError(message || 'Error updating meta app');
                setSaving(false);
            },
            onFinally: () => setSaving(false)
        });
    };

    // Delete open
    const handleDeleteOpen = (row) => {
        setSelectedRow(row);
        setDeleteOpen(true);
    };

    // Delete confirm
    const handleDeleteConfirm = () => {
        setDeleting(true);
        request({
            module: 'saChannels',
            method: 'metaAppDelete',
            payload: { id: selectedRow.id },
            onSuccess: () => {
                showSuccess('Meta app deleted successfully');
                setDeleteOpen(false);
                setSelectedRow(null);
                fetchMetaApps();
                setDeleting(false);
            },
            onError: (message) => {
                showError(message || 'Error deleting meta app');
                setDeleting(false);
            },
            onFinally: () => setDeleting(false)
        });
    };

    // Config pair handlers
    const handleAddConfigPair = () => {
        setFormData(prev => ({
            ...prev,
            configPairs: [...prev.configPairs, { key: '', value: '', required: false }]
        }));
    };

    const handleRemoveConfigPair = (index) => {
        setFormData(prev => {
            const pair = prev.configPairs[index];
            if (pair?.required) return prev;

            // Don't allow removing the last remaining config_id pair
            if (pair?.isConfigId) {
                const remainingConfigIds = prev.configPairs.filter(
                    (p, i) => i !== index && p.isConfigId
                );
                if (remainingConfigIds.length === 0) {
                    showWarning('At least one config_id (whatsapp/facebook/instagram) is required');
                    return prev;
                }
            }

            return {
                ...prev,
                configPairs: prev.configPairs.filter((_, i) => i !== index)
            };
        });
    };

    const handleConfigPairChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            configPairs: prev.configPairs.map((pair, i) =>
                i === index ? { ...pair, [field]: value } : pair
            )
        }));
    };

    const webhookUrlInvalid = formData.webhookUrl.trim() !== '' && !isValidUrl(formData.webhookUrl);
    const appVersionInvalid = formData.appVersion.trim() !== '' && !isValidAppVersion(formData.appVersion);

    const isFormInvalid = () => {
        const hasEmptyConfigPairs = formData.configPairs.some(
            pair => !pair.key.trim() || !pair.value.trim()
        );
        const missingRequired = formData.configPairs.some(
            pair => pair.required && !pair.value.trim()
        );
        const hasAtLeastOneConfigId = formData.configPairs.some(
            pair => pair.isConfigId && pair.value.trim()
        );
        return !formData.name.trim() || !formData.appId.trim() || !formData.appSecret.trim() || !formData.appVersion.trim() || saving || hasEmptyConfigPairs || missingRequired || !hasAtLeastOneConfigId || nameExists || webhookUrlInvalid || appVersionInvalid;
    };

    // Table columns
    const headCells = [
        { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
        { id: 'name', numeric: false, disablePadding: false, label: 'Name', tooltip: true },
        { id: 'app_id', numeric: false, disablePadding: false, label: 'App ID', tooltip: true },
        { id: 'app_secret', numeric: false, disablePadding: false, label: 'App Secret', tooltip: true, msg: true, truncateLength: 20 },
        { id: 'app_version', numeric: false, disablePadding: false, label: 'App Version' },
        { id: 'webhook_url', numeric: false, disablePadding: false, label: 'Webhook URL', tooltip: true, msg: true, truncateLength: 30 },
        { id: 'webhook_verify_token', numeric: false, disablePadding: false, label: 'Webhook Verify Token', tooltip: true, msg: true, truncateLength: 20 },
        {
            id: 'status',
            numeric: false,
            disablePadding: false,
            label: 'Status',
            pill: true,
            pillConfig: {
                mapping: {
                    active: {
                        label: 'Active',
                        color: '#4caf50',
                        bgColor: 'rgba(76, 175, 80, 0.1)',
                        icon: <CheckCircleIcon fontSize="small" />
                    },
                    inactive: {
                        label: 'Inactive',
                        color: '#f44336',
                        bgColor: 'rgba(244, 67, 54, 0.1)',
                        icon: <CancelIcon fontSize="small" />
                    }
                },
                defaultColor: '#9e9e9e',
                defaultBgColor: 'rgba(158, 158, 158, 0.1)'
            }
        },
        {
            id: 'configuration',
            numeric: false,
            disablePadding: false,
            label: 'Configuration',
            msg: true,
            truncateLength: 30,
            customRow: row => row.configuration ? JSON.stringify(row.configuration) : '-'
        },
        { id: 'dt', numeric: false, disablePadding: false, label: 'Created', dt: true },
        { id: 'dtu', numeric: false, disablePadding: false, label: 'Updated', dt: true },
        { id: 'edit', numeric: false, disablePadding: false, label: 'Edit' },
        { id: 'delete', numeric: false, disablePadding: false, label: 'Delete' }
    ];

    // Form content for create/edit dialog
    const renderFormContent = () => (
        <div className={classes.formContent}>
            <FormFieldOmni
                label="App Name *"
                variant="outlined"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                type="text"
                size="small"
                fullWidth
                multiline
                rows={calcRows(formData.name)}
                inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                placeholder="e.g. My Facebook App"
            />
            {nameExists && (
                <Typography variant="caption" style={{ color: '#f44336', marginTop: -4 }}>
                    App name already exists
                </Typography>
            )}
            <FormFieldOmni
                label="App ID *"
                variant="outlined"
                value={formData.appId}
                onChange={(e) => handleFormChange('appId', e.target.value)}
                type="text"
                size="small"
                fullWidth
                multiline
                rows={calcRows(formData.appId)}
                inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                placeholder="e.g. 0000000000000000000"
            />
            <FormFieldOmni
                label="App Secret *"
                variant="outlined"
                value={formData.appSecret}
                onChange={(e) => handleFormChange('appSecret', e.target.value)}
                type="text"
                size="small"
                fullWidth
                multiline
                rows={calcRows(formData.appSecret)}
                inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                placeholder="e.g. 0000000000000000000"
            />
            <FormFieldOmni
                label="App Version *"
                variant="outlined"
                value={formData.appVersion}
                onChange={(e) => handleFormChange('appVersion', e.target.value)}
                type="text"
                size="small"
                fullWidth
                multiline
                rows={calcRows(formData.appVersion)}
                inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                placeholder="e.g. v24.0"
            />
            {appVersionInvalid && (
                <Typography variant="caption" style={{ color: '#f44336', marginTop: -4 }}>
                    Invalid version format (e.g. v24.0, v33.2)
                </Typography>
            )}
            <FormFieldOmni
                label="Webhook URL"
                variant="outlined"
                value={formData.webhookUrl}
                onChange={(e) => handleFormChange('webhookUrl', e.target.value)}
                type="text"
                size="small"
                fullWidth
                multiline
                rows={calcRows(formData.webhookUrl)}
                inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                placeholder="e.g. https://wacloudcallback.com/webhook/cloud"
            />
            {webhookUrlInvalid && (
                <Typography variant="caption" style={{ color: '#f44336', marginTop: -4 }}>
                    Please enter a valid URL (e.g. https://example.com)
                </Typography>
            )}
            <FormFieldOmni
                label="Webhook Verify Token"
                variant="outlined"
                value={formData.webhookVerifyToken}
                onChange={(e) => handleFormChange('webhookVerifyToken', e.target.value)}
                type="text"
                size="small"
                fullWidth
                multiline
                rows={calcRows(formData.webhookVerifyToken)}
                inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                placeholder="Optional"
            />
            <FormFieldOmni
                label="Webhook Secret"
                variant="outlined"
                value={formData.webhookSecret}
                onChange={(e) => handleFormChange('webhookSecret', e.target.value)}
                type="text"
                size="small"
                fullWidth
                multiline
                rows={calcRows(formData.webhookSecret)}
                inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                placeholder="Optional"
            />

            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 8 }}>
                <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 600 }}
                    color="textPrimary"
                >
                    Configuration
                </Typography>
                <ButtonOmni
                    onClick={handleAddConfigPair}
                    variant="outlined"
                    label="+ Add"
                    size="small"
                />
            </Box>
            {formData.configPairs.map((pair, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1} style={{ gap: 8 }}>
                    <FormFieldOmni
                        label={(pair.required || pair.isConfigId) ? 'Key *' : 'Key'}
                        variant="outlined"
                        value={pair.key}
                        onChange={(e) => handleConfigPairChange(index, 'key', e.target.value)}
                        type="text"
                        size="small"
                        fullWidth
                        multiline
                        rows={calcRows(pair.key)}
                        inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                        placeholder="e.g. whatsapp_config_id"
                        disabled={pair.required || pair.isConfigId}
                    />
                    <FormFieldOmni
                        label={pair.required ? 'Value *' : 'Value'}
                        variant="outlined"
                        value={pair.value}
                        onChange={(e) => handleConfigPairChange(index, 'value', e.target.value)}
                        type="text"
                        size="small"
                        fullWidth
                        multiline
                        rows={calcRows(pair.value)}
                        inputProps={{ style: { fontSize: '14px', color: theme.palette.text.primary, resize: 'vertical' } }}
                        placeholder="e.g. 0000000000000000000"
                    />
                    {!pair.required && (
                        <DeleteIcon
                            fontSize="large"
                            style={{ cursor: 'pointer', color: theme.palette.secondary.value, flexShrink: 0 }}
                            onClick={() => handleRemoveConfigPair(index)}
                        />
                    )}
                </Box>
            ))}
        </div>
    );

    // Create dialog actions
    const createActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmni onClick={() => { setCreateOpen(false); resetForm(); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmni onClick={handleCreate} variant="outlined" label={saving ? 'Adding...' : 'Add'} disabled={isFormInvalid()} />
        </div>
    );

    // Edit dialog actions
    const editActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmni onClick={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmni onClick={handleUpdate} variant="outlined" label={saving ? 'Updating...' : 'Update'} disabled={isFormInvalid()} />
        </div>
    );

    return (
        <div className="w-full mx-auto">
            <DateFilterWithExport
                showDateRange={false}
                showExport={false}
                filterFields={[]}
                filterValues={{}}
                onFiltersApply={() => { }}
                onFiltersClear={() => { }}
                showRefresh={true}
                onRefresh={handleRefresh}
                customRightContent={
                    <ButtonOmniNew
                        buttonType="create"
                        label="Add Meta App"
                        onClick={() => { resetForm(); setCreateOpen(true); }}
                        variant="outlined"
                    />
                }
            />

            <TableOmniNewAnimated
                rows={data}
                headCells={headCells}
                perPageRows={currentParams.limit}
                setCurrentParams={setCurrentParams}
                totalItems={totalItems}
                curPage={currentParams.page}
                loading={isLoading}
                onEditClick={handleEditOpen}
                onDeleteClick={handleDeleteOpen}
            />

            <ModalOmni
                disableBackdropClick
                open={createOpen}
                handleClose={() => { setCreateOpen(false); resetForm(); }}
                title="Add Meta App"
                content={renderFormContent()}
                actions={createActions}
                customWidth={500}
            />

            <ModalOmni
                disableBackdropClick
                open={editOpen}
                handleClose={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }}
                title="Edit Meta App"
                content={renderFormContent()}
                actions={editActions}
                customWidth={500}
            />

            <DeleteConfirmationDialog
                open={deleteOpen}
                onClose={() => { setDeleteOpen(false); setSelectedRow(null); }}
                onConfirm={handleDeleteConfirm}
                title="Delete Meta App"
                message={`Are you sure you want to delete the meta app "${selectedRow?.name || ''}"? This action cannot be undone.`}
                loading={deleting}
            />

            <SnackbarOmni {...snackbar} />
        </div>
    );
}

export default React.memo(MetaAppsTab);